import { useState } from "react";
import { api } from "@/lib/api";
import { BUSINESS } from "@/lib/constants";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [tab, setTab] = useState("inquiry");
  const [contact, setContact] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [quote, setQuote] = useState({ name: "", email: "", phone: "", company: "", items: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submitContact = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/contact", contact);
      toast.success("Message sent. We'll respond shortly.");
      setContact({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch { toast.error("Failed to send"); }
    finally { setBusy(false); }
  };
  const submitQuote = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/quote-request", quote);
      toast.success("Quote request submitted!");
      setQuote({ name: "", email: "", phone: "", company: "", items: "", message: "" });
    } catch { toast.error("Failed to send"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <section className="bg-slate-950 text-white py-20">
        <div className="max-w-4xl mx-auto text-center px-4">
          <div className="text-sm uppercase tracking-widest text-blue-300 font-semibold mb-2">Contact</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-balance">We're happy to help.</h1>
          <p className="mt-4 text-slate-300">Talk to our team about products, service, AMC, or a custom quote.</p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[380px_1fr] gap-10">
          <aside className="space-y-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-soft">
              <div className="font-heading font-semibold mb-4">Reach us directly</div>
              <ul className="space-y-4 text-sm">
                <li className="flex gap-3"><MapPin className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" /> <div>{BUSINESS.address}</div></li>
                <li className="flex gap-3"><Phone className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" /> <a href={`tel:${BUSINESS.phone}`}>{BUSINESS.phone}</a></li>
                <li className="flex gap-3"><Mail className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" /> <a href={`mailto:${BUSINESS.email}`}>{BUSINESS.email}</a></li>
                <li className="flex gap-3"><Clock className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" /> <div>{BUSINESS.hours}</div></li>
              </ul>
              <a href={`https://wa.me/${BUSINESS.whatsapp}`} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium" data-testid="contact-whatsapp">
                <MessageCircle className="w-4 h-4" /> WhatsApp us
              </a>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <iframe title="map" src={BUSINESS.mapEmbed} width="100%" height="260" loading="lazy" className="border-0"></iframe>
            </div>
          </aside>

          <div>
            <div className="flex border-b border-slate-200 mb-8">
              {[{k:"inquiry",l:"General Inquiry"},{k:"quote",l:"Request a Quote"}].map((t) => (
                <button key={t.k} onClick={() => setTab(t.k)} className={`px-5 py-3 font-medium text-sm ${tab === t.k ? "text-blue-900 border-b-2 border-blue-900 -mb-px" : "text-slate-500"}`} data-testid={`contact-tab-${t.k}`}>
                  {t.l}
                </button>
              ))}
            </div>
            {tab === "inquiry" ? (
              <form onSubmit={submitContact} className="bg-white border border-slate-200 rounded-2xl p-8 space-y-4 shadow-soft">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required placeholder="Name" value={contact.name} onChange={(e) => setContact({...contact, name: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="cont-name" />
                  <input required type="email" placeholder="Email" value={contact.email} onChange={(e) => setContact({...contact, email: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="cont-email" />
                  <input placeholder="Phone" value={contact.phone} onChange={(e) => setContact({...contact, phone: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="cont-phone" />
                  <input placeholder="Subject" value={contact.subject} onChange={(e) => setContact({...contact, subject: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="cont-subject" />
                </div>
                <textarea required rows={5} placeholder="Your message..." value={contact.message} onChange={(e) => setContact({...contact, message: e.target.value})} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="cont-message"></textarea>
                <button disabled={busy} className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 rounded-xl font-medium disabled:opacity-60" data-testid="cont-submit">
                  {busy ? "Sending..." : "Send Message"}
                </button>
              </form>
            ) : (
              <form onSubmit={submitQuote} className="bg-white border border-slate-200 rounded-2xl p-8 space-y-4 shadow-soft">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required placeholder="Full name" value={quote.name} onChange={(e) => setQuote({...quote, name: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="qt-name" />
                  <input required type="email" placeholder="Email" value={quote.email} onChange={(e) => setQuote({...quote, email: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="qt-email" />
                  <input required placeholder="Phone" value={quote.phone} onChange={(e) => setQuote({...quote, phone: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="qt-phone" />
                  <input placeholder="Company (optional)" value={quote.company} onChange={(e) => setQuote({...quote, company: e.target.value})} className="border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="qt-company" />
                </div>
                <textarea required rows={3} placeholder="What products/services do you need a quote for?" value={quote.items} onChange={(e) => setQuote({...quote, items: e.target.value})} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="qt-items"></textarea>
                <textarea rows={3} placeholder="Additional notes..." value={quote.message} onChange={(e) => setQuote({...quote, message: e.target.value})} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm" data-testid="qt-message"></textarea>
                <button disabled={busy} className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 rounded-xl font-medium disabled:opacity-60" data-testid="qt-submit">
                  {busy ? "Sending..." : "Request Quote"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
